import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { Injectable, NgZone } from '@angular/core';
import { catchError, map } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { StorageService } from '../storage/storage.service';
import { Device } from '@awesome-cordova-plugins/device/ngx';
import OneSignal, { NotificationClickEvent, UserChangedState } from 'onesignal-cordova-plugin';
import { Router } from '@angular/router';
import { OSNotification } from 'onesignal-cordova-plugin/dist/OSNotification';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SnackbarNotificationComponent } from 'src/app/components/snackbar-notification/snackbar-notification.component';
import * as moment from 'moment';
import { AppDevice } from 'src/app/models/appDevice.model';
import { AppSettingsService } from '../app-settings/app-settings.service';
import { Preferences } from '@capacitor/preferences';

@Injectable({
  providedIn: 'root'
})
export class DeviceService {

  constructor(
    private router: Router,
    private device: Device,
    private http: HttpClient,
    private storage: StorageService,
    private ngZone: NgZone,
    private snackBar: MatSnackBar,
    private settings: AppSettingsService,
  ) { }

  getDevice(deviceID: string, appUserID: number): Promise<AppDevice>  {
    return this.storage.get("device").then(device => {
      if(device && device.id) {
        return new AppDevice(device);
      } else {
        const params = {
          'device_id': deviceID,
          'app_user_id': appUserID
        }

        let observable: Observable<AppDevice> = this.http.get(this.settings.apiUrl + `appDevice?parameters=` + JSON.stringify(params)).pipe(
          map((data: any) => {
            if(!data) {
              return null;
            } else {
              this.storage.set("device", data);
              return new AppDevice(data);
            }
          })
        );
        return observable.toPromise();
      }
    });
  }

  updateDevice(device: AppDevice): Observable<AppDevice> {

    const body = {
      'device': device
    }

    return this.http.put(this.settings.apiUrl + `appDevice/${device.id}`, body).pipe(
      map((data: any) => {
        this.storage.set("device", device);
        return new AppDevice(data);
      })
    );
  }

  /**
   * Generates a unique ("cross-install, app-specific") ID for the device
   */
  generateDeviceId(platform: any, uid: string) {
    if(platform == "web") {
      return Promise.resolve(uid + 'web');
    } else {
      return Promise.resolve(this.device.uuid);
    }
  }

  /**
   * Get the id of the device type
   */
  getDeviceType(platform: string): Observable<any> {
    const params = {
      'device_name': platform,
    }

    return this.http.get(this.settings.apiUrl + `deviceTypes?parameters=` + JSON.stringify(params)).pipe(
      map((data: any) => {
        return data[0].id
      }),
      catchError(err => {
        return err.error;
      })
    );
  }

  /**
   * Add device to AppDevices
   */
  addDevice(appUserId: number, id: string, deviceTypeId: string) {
    const prefersDark = window.matchMedia("(prefers-color-scheme: dark)");
    return this.http.post(this.settings.apiUrl + `appDevice`, { 'app_user_id': appUserId, 'app_device_id': id, 'device_type_id': deviceTypeId }).pipe(
      map((data: any) => {
        if (data?.device && data.token) {
          this.storage.set("device", data.device);
          this.storage.set("Session_Token", data.token);
          return new AppDevice(data.device);
        }
      }),
      catchError(err => {
        const error = err.error.message || err.error.detail || err.statusText;
        return error;
      })
    );
  }

  oneSignalInit(appDevice: AppDevice): void {
    // Uncomment to set OneSignal device logging to VERBOSE
    // OneSignal.setLogLevel(6, 0);
    if(this.device?.platform) {
      OneSignal.initialize(environment.ONESIGNAL_APP_ID); // OneSignal AppId.

      OneSignal.Notifications.addEventListener("click", (event: NotificationClickEvent) => {
        this.notificationOpened(event.notification)
      })

      OneSignal.User.addEventListener("change", (event: UserChangedState) => {
        if (event.current.onesignalId) {
          appDevice.oneSignalID = event.current.onesignalId;
          this.updateDevice(appDevice).subscribe();
        }
      })
      
      OneSignal.Notifications.requestPermission(false).then(accepted => {
        if (accepted) {
          OneSignal.login(appDevice.appUserID.toString());
          OneSignal.User.pushSubscription.optIn();
        }
      });
    }
  }

  getDeviceID(): Promise<string> {
    var promise = new Promise<string>((resolve, reject) => {
      OneSignal.User.getOnesignalId().then((id: string) => {
        if (id) {
          resolve(id);
        } else {
          resolve(null);
        }
      })
    })

    return promise;
  }

  notificationOpened(notification: OSNotification) {
    this.ngZone.run(() => {
      let data = notification?.additionalData;
      if (data["type"] == "BROADCAST") {
        // set a time delay to allow portal page to load if user only has one portal
        setTimeout(() => {
          this.router.navigateByUrl('/home/view-post/' + data["entityID"]);
        }, 100);
      } else if (data["type"] == "STAFF_POST") {
        setTimeout(() => {
          this.router.navigateByUrl('/home/view-post/' + data["entityID"]);
        }, 100);
      } else if (data["type"] == "POST_COMMENT") {
        setTimeout(() => {
          this.router.navigateByUrl('/home/view-post/' + data["entityID"]);
        }, 100);
      } else if (data["type"] == "MESSAGE") {
        this.router.navigateByUrl("/home/portal/" + data["portalID"]);
        setTimeout(() => {
          this.router.navigateByUrl('/messaging/' + data["portalID"] + "/" + data["entityID"]);
        }, 100);
      } else if (data["type"] == "EVENT_REMINDER") {
        setTimeout(() => {
          this.router.navigateByUrl('/home/calendar/' + data["portalID"] + "?event=" + data["entityID"]);
        }, 100);
      } else if (data["type"] == "PORTAL_SUMMARY") {
        setTimeout(() => {
          this.router.navigateByUrl("/home/portal/" + data["portalID"]);
        }, 100);
      } else if (data["type"] == "NZ_REPORT") {
        setTimeout(() => {
          this.router.navigateByUrl("/home/profile/" + data["entityID"]);
        }, 100);
      }
    });
  }

  async showInAppNotification(notification: OSNotification): Promise<void> {
    let content = notification.body;

    // Convert date from reminder into local time
    if (notification.additionalData["type"] == "EVENT_REMINDER") {
      let localDate = moment(notification.additionalData["utcDate"]);
      let dateString = localDate.format("MMM, D, YYYY");
      let timeString = localDate.format("h:mm A");

      content = dateString + " at " + timeString;
    }

    let snackbar = this.snackBar.openFromComponent(SnackbarNotificationComponent, {
      panelClass: ['snackbar'],
      data: {
        header: notification.title,
        content: content,
        image: notification.attachments["image"]
      },
      verticalPosition: "top"
    });

    snackbar.onAction().subscribe(() => {
      this.notificationOpened(notification);
    });
  }
}
